/**
 * Page de gestion de la connexion WhatsApp
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  QrCode as QrCodeIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  PowerSettingsNew as DisconnectIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';

const WhatsAppConfig = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [qrCountdown, setQrCountdown] = useState(20);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('🗓️ Test Éphéméride Duala\n\nCeci est un message de test.');
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);

  // Charger le statut WhatsApp
  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get('/whatsapp/status');
      setStatus(response.data);
      
      // Si en attente de QR, charger le QR code
      if (response.data.status === 'qr_pending') {
        const qrResponse = await api.get('/whatsapp/qr');
        setQrCode(qrResponse.data.qrCode);
      } else {
        setQrCode(null);
      }
    } catch (error) {
      console.error('Erreur chargement statut:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les statistiques
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/whatsapp/stats');
      // Le backend renvoie { success: true, data: { total, sent, ... } }
      setStats(response.data.data || response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchStats();

    // Rafraîchir le statut toutes les 3 secondes
    const interval = setInterval(() => {
      fetchStatus();
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchStatus, fetchStats]);

  // Countdown 20s remis à zéro à chaque nouveau QR
  useEffect(() => {
    if (!qrCode) { setQrCountdown(20); return; }
    setQrCountdown(20);
    const tick = setInterval(() => {
      setQrCountdown(prev => (prev <= 1 ? 20 : prev - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, [qrCode]);

  // Initialiser WhatsApp
  const handleInitialize = async () => {
    setInitializing(true);
    try {
      await api.post('/whatsapp/initialize');
      enqueueSnackbar('Initialisation WhatsApp lancée', { variant: 'info' });
      // Attendre un peu puis rafraîchir
      setTimeout(fetchStatus, 2000);
    } catch (error) {
      enqueueSnackbar(`Erreur: ${error.response?.data?.error || error.message}`, { variant: 'error' });
    } finally {
      setInitializing(false);
    }
  };

  // Déconnecter WhatsApp
  const handleDisconnect = async () => {
    try {
      await api.post('/whatsapp/disconnect');
      enqueueSnackbar('WhatsApp déconnecté', { variant: 'success' });
      fetchStatus();
    } catch (error) {
      enqueueSnackbar(`Erreur: ${error.response?.data?.error || error.message}`, { variant: 'error' });
    }
  };

  // Envoyer un message test
  const handleSendTest = async () => {
    if (!testPhone || !testMessage) {
      enqueueSnackbar('Veuillez remplir tous les champs', { variant: 'warning' });
      return;
    }

    setSending(true);
    try {
      const response = await api.post('/whatsapp/test', {
        phoneNumber: testPhone,
        message: testMessage
      });
      
      if (response.data.success) {
        enqueueSnackbar('Message envoyé avec succès !', { variant: 'success' });
        setTestDialogOpen(false);
      } else if (response.data.queued) {
        enqueueSnackbar('Message en file d\'attente (WhatsApp non connecté)', { variant: 'warning' });
      } else {
        enqueueSnackbar(`Erreur: ${response.data.error}`, { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(`Erreur: ${error.response?.data?.error || error.message}`, { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  // Rendu du statut avec couleur
  const renderStatusChip = () => {
    if (!status) return null;

    const statusConfig = {
      'connected': { color: 'success', label: 'Connecté', icon: <CheckIcon /> },
      'qr_pending': { color: 'warning', label: 'En attente de scan QR', icon: <QrCodeIcon /> },
      'authenticated': { color: 'info', label: 'Authentifié', icon: <CheckIcon /> },
      'disconnected': { color: 'default', label: 'Déconnecté', icon: <ErrorIcon /> },
      'error': { color: 'error', label: 'Erreur', icon: <ErrorIcon /> },
      'auth_failed': { color: 'error', label: 'Authentification échouée', icon: <ErrorIcon /> }
    };

    const config = statusConfig[status.status] || statusConfig['disconnected'];

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="large"
        sx={{ fontSize: '1rem', py: 2.5 }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WhatsAppIcon color="success" fontSize="large" />
        Configuration WhatsApp
      </Typography>

      <Grid container spacing={3}>
        {/* Statut de connexion */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statut de connexion
              </Typography>
              
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                {renderStatusChip()}
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={fetchStatus}
                >
                  Actualiser
                </Button>
              </Box>

              {status?.lastError && !['connected', 'authenticated'].includes(status?.status) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {status.lastError}
                </Alert>
              )}

              {status?.queueLength > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {status.queueLength} message(s) en file d'attente
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {status?.status !== 'connected' && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={initializing ? <CircularProgress size={18} color="inherit" /> : <WhatsAppIcon />}
                    onClick={handleInitialize}
                    disabled={initializing}
                  >
                    {initializing
                      ? 'Initialisation...'
                      : status?.status === 'qr_pending'
                        ? 'Relancer (nouveau QR)'
                        : 'Connecter WhatsApp'}
                  </Button>
                )}

                {status?.status === 'connected' && (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<SendIcon />}
                      onClick={() => setTestDialogOpen(true)}
                    >
                      Envoyer un test
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DisconnectIcon />}
                      onClick={handleDisconnect}
                    >
                      Déconnecter
                    </Button>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* QR Code */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                QR Code WhatsApp
              </Typography>

              {qrCode ? (
                <Box sx={{ textAlign: 'center' }}>
                  {/* Barre de progression du countdown */}
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{
                      height: 6, borderRadius: 3, bgcolor: '#e0e0e0', overflow: 'hidden'
                    }}>
                      <Box sx={{
                        height: '100%',
                        width: `${(qrCountdown / 20) * 100}%`,
                        bgcolor: qrCountdown <= 5 ? 'error.main' : 'success.main',
                        transition: 'width 1s linear, background-color 0.3s',
                        borderRadius: 3,
                      }} />
                    </Box>
                    <Typography variant="caption" color={qrCountdown <= 5 ? 'error' : 'text.secondary'}>
                      QR valide encore {qrCountdown}s — se rafraîchit automatiquement
                    </Typography>
                  </Box>
                  <img
                    key={qrCode}
                    src={qrCode}
                    alt="QR Code WhatsApp"
                    style={{ maxWidth: '100%', maxHeight: '280px', border: '3px solid #25D366', borderRadius: 8 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Scannez avec WhatsApp → Menu → Appareils connectés
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  {status?.status === 'connected' ? (
                    <Alert severity="success">
                      WhatsApp est connecté ! Pas besoin de QR code.
                    </Alert>
                  ) : (
                    <Typography color="text.secondary">
                      Cliquez sur "Connecter WhatsApp" pour générer le QR code
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Statistiques */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistiques des messages
              </Typography>

              {stats ? (
                <List>
                  <ListItem>
                    <ListItemIcon><SendIcon /></ListItemIcon>
                    <ListItemText primary="Total envoyés" secondary={stats.total} />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Envoyés avec succès" secondary={stats.sent} />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon><CheckIcon color="info" /></ListItemIcon>
                    <ListItemText primary="Délivrés" secondary={stats.delivered} />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                    <ListItemText primary="Échoués" secondary={stats.failed} />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Taux de réussite" 
                      secondary={`${stats.successRate}%`}
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                </List>
              ) : (
                <Typography color="text.secondary">Chargement...</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Instructions
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemIcon>1.</ListItemIcon>
                  <ListItemText primary="Cliquez sur 'Connecter WhatsApp'" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>2.</ListItemIcon>
                  <ListItemText primary="Ouvrez WhatsApp sur votre téléphone" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>3.</ListItemIcon>
                  <ListItemText primary="Allez dans Menu → Appareils connectés" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>4.</ListItemIcon>
                  <ListItemText primary="Cliquez sur 'Connecter un appareil'" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>5.</ListItemIcon>
                  <ListItemText primary="Scannez le QR code affiché" />
                </ListItem>
              </List>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>Important :</strong> Gardez votre téléphone connecté à Internet pour que WhatsApp fonctionne.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog pour envoyer un message test */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon />
            Envoyer un message test
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Numéro de téléphone"
            placeholder="+33612345678"
            fullWidth
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            helperText="Format international avec indicatif pays"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Annuler</Button>
          <Button 
            onClick={handleSendTest} 
            variant="contained" 
            color="success"
            disabled={sending}
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sending ? 'Envoi...' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppConfig;
